import * as React from "react";
import { createBrowserRouter, Outlet } from "react-router-dom";
import Root from "@/app/root";
import { RequireAuth } from "@/components/shell/require-auth";
import { RequireOrganisation } from "@/components/shell/require-organisation";
import RouteError from "@/app/route-error";
import { PageLoading } from "@/components/shell/page-loading";

/**
 * Every PAGE loads when it is first visited (2.713). The shell (root, layouts,
 * the auth guard, the error page) stays in the entry chunk, because every page
 * needs it; each page is its own chunk, so the first load carries one page's
 * code rather than all of them. Measured before and after in the plan row.
 */
function page(load: () => Promise<{ default: React.ComponentType }>) {
  return async () => ({ Component: (await load()).default });
}

import AuthLayout from "@/app/(auth)/layout";
import AccountLayout from "@/app/(account)/layout";
import PlatformLayout from "@/app/(platform)/layout";
import ProjectLayout from "@/app/(app)/projects/[projectId]/layout";

/**
 * Next's route groups — `(auth)`, `(account)`, `(app)`, `(platform)` — were
 * layouts that wrapped their segment's children. Each becomes a pathless route
 * whose element renders that same layout around an `<Outlet />`, so the URL
 * shape and the layout nesting are both preserved exactly.
 */
function layoutRoute(Layout: React.ComponentType<{ children: React.ReactNode }>) {
  return function LayoutRoute() {
    return (
      <Layout>
        <Outlet />
      </Layout>
    );
  };
}

const AuthRoute = layoutRoute(AuthLayout);
const AccountRoute = layoutRoute(AccountLayout);
const PlatformRoute = layoutRoute(PlatformLayout);
const ProjectRoute = layoutRoute(ProjectLayout);

export const routes = [
  {
    element: <Root />,
    // Catches an unmatched URL (React Router raises a 404 ErrorResponse) and any
    // unhandled render error beneath it. Not a `path: "*"` entry, so the route
    // table still maps one-to-one onto the page tree — see test/routes.test.ts.
    errorElement: <RouteError />,
    // The first page's code is still on its way: said, and announced (2.713).
    hydrateFallbackElement: <PageLoading />,
    children: [
      { path: "/", lazy: page(() => import("@/app/(marketing)/page")) },

      {
        element: <AuthRoute />,
        children: [
          { path: "/login", lazy: page(() => import("@/app/(auth)/login/page")) },
          { path: "/reset-password", lazy: page(() => import("@/app/(auth)/reset-password/page")) },
          { path: "/accept-invite/:token", lazy: page(() => import("@/app/(auth)/accept-invite/[token]/page")) },
          { path: "/callback", lazy: page(() => import("@/app/(auth)/callback/page")) },
        ],
      },

      {
        // LAND BY ROLE (the sponsor, 2026-10-14): where a sign-in lands when no page
        // was asked for. An operator goes to /platform, everyone else to /overview.
        path: "/home",
        element: <RequireAuth><Outlet /></RequireAuth>,
        children: [{ index: true, lazy: page(() => import("@/components/shell/land-by-role")) }],
      },

      {
        // 3.143: an account with no organisation goes to /platform, never a tenant page.
        element: <RequireAuth><RequireOrganisation><AccountRoute /></RequireOrganisation></RequireAuth>,
        children: [
          { path: "/overview", lazy: page(() => import("@/app/(account)/overview/page")) },
          // IFC-016's list verb has TWO addresses. This one is org-scoped:
          // `GET /v1/issues` carries no project segment and the gateway resolves
          // which projects the caller may view, so it cannot refuse — a project
          // the caller cannot see is simply absent. The per-project view is a
          // real second address (`projects/:projectId/issues` below) rather than
          // this list filtered client-side, which would page the whole
          // organisation to render one project.
          { path: "/issues", lazy: page(() => import("@/app/(account)/issues/page")) },
          { path: "/issues/:id", lazy: page(() => import("@/app/(account)/issues/[id]/page")) },
          // FR-09 — org-scoped keys (DAT-026). Spec-derived until CMP-010 is served.
          { path: "/api-keys", lazy: page(() => import("@/app/(account)/api-keys/page")) },
          // FR-67 / FR-69 — IFC-018's subscriptions, deliveries and alert rules.
          { path: "/alerts", lazy: page(() => import("@/app/(account)/alerts/page")) },
          // FR-59, ADR-086 — the organisation's risk weights.
          { path: "/risk-weights", lazy: page(() => import("@/app/(account)/risk-weights/page")) },
          // FR-54a, S-612 — which findings become issues automatically.
          { path: "/promotion-rule", lazy: page(() => import("@/app/(account)/promotion-rule/page")) },
          // FR-68's in-app channel (ADR-075): the caller's own and the organisation-wide notifications.
          { path: "/notifications", lazy: page(() => import("@/app/(account)/notifications/page")) },
          { path: "/projects", lazy: page(() => import("@/app/(account)/projects/page")) },
          { path: "/members", lazy: page(() => import("@/app/(account)/members/page")) },
          { path: "/roles", lazy: page(() => import("@/app/(account)/roles/page")) },
          { path: "/ai-testing", lazy: page(() => import("@/app/(account)/ai-testing/page")) },
          // FR-64 (2.744): the organisation's trends, over the projects the caller may see.
          { path: "/trends", lazy: page(() => import("@/app/(account)/trends/page")) },
          { path: "/account", lazy: page(() => import("@/app/(account)/account/page")) },
          // S-709, ADR-106 (2.790): the caller's own effective permissions; any member.
          { path: "/my-permissions", lazy: page(() => import("@/app/(account)/my-permissions/page")) },
        ],
      },

      {
        path: "/projects/:projectId",
        element: <RequireAuth><RequireOrganisation><ProjectRoute /></RequireOrganisation></RequireAuth>,
        children: [
          { path: "dashboard", lazy: page(() => import("@/app/(app)/projects/[projectId]/dashboard/page")) },
          { path: "reports", lazy: page(() => import("@/app/(app)/projects/[projectId]/reports/page")) },
          { path: "scans", lazy: page(() => import("@/app/(app)/projects/[projectId]/scans/page")) },
          // FR-23 — IFC-014's schedules. Not a launch option; the form no longer pretends it is.
          { path: "schedules", lazy: page(() => import("@/app/(app)/projects/[projectId]/schedules/page")) },
          // FR-64 (2.744): one project's trends.
          { path: "trends", lazy: page(() => import("@/app/(app)/projects/[projectId]/trends/page")) },
          { path: "scans/new", lazy: page(() => import("@/app/(app)/projects/[projectId]/scans/new/page")) },
          { path: "scans/:id", lazy: page(() => import("@/app/(app)/projects/[projectId]/scans/[id]/page")) },
          { path: "targets", lazy: page(() => import("@/app/(app)/projects/[projectId]/targets/page")) },
          { path: "targets/add", lazy: page(() => import("@/app/(app)/projects/[projectId]/targets/add/page")) },
          { path: "targets/:id", lazy: page(() => import("@/app/(app)/projects/[projectId]/targets/[id]/page")) },
          { path: "targets/:id/auth-profiles", lazy: page(() => import("@/app/(app)/projects/[projectId]/targets/[id]/auth-profiles/page")) },
          // FR-16 — IFC-013's schema import: a target's declared operations (not yet used in scans).
          { path: "targets/:id/schema-import", lazy: page(() => import("@/app/(app)/projects/[projectId]/targets/[id]/schema-import/page")) },
          // IFC-015's detail verb, addressed by finding id alone — the
          // platform's own address. Reached from a run, which is the only
          // place the gateway lists findings.
          { path: "findings/:id", lazy: page(() => import("@/app/(app)/projects/[projectId]/findings/[id]/page")) },
          { path: "vulnerabilities", lazy: page(() => import("@/app/(app)/projects/[projectId]/vulnerabilities/page")) },
          // IFC-016's list verb addressed at THIS project. A real address —
          // `GET /v1/projects/{id}/issues` — authorized on the project in the
          // path, so it refuses a caller with no relation on it rather than
          // silently omitting the rows. The detail route stays org-scoped at
          // `/issues/:id`: an issue is read by its own id and carries no project
          // segment, the same split scan runs use.
          { path: "issues", lazy: page(() => import("@/app/(app)/projects/[projectId]/issues/page")) },
          { path: "settings", lazy: page(() => import("@/app/(app)/projects/[projectId]/settings/page")) },
          { path: "settings/ai-testing", lazy: page(() => import("@/app/(app)/projects/[projectId]/settings/ai-testing/page")) },
          { path: "settings/excluded-hours", lazy: page(() => import("@/app/(app)/projects/[projectId]/settings/excluded-hours/page")) },
          { path: "settings/scan-profiles", lazy: page(() => import("@/app/(app)/projects/[projectId]/settings/scan-profiles/page")) },
        ],
      },

      {
        element: <RequireAuth><PlatformRoute /></RequireAuth>,
        children: [
          { path: "/platform", lazy: page(() => import("@/app/(platform)/platform/page")) },
          { path: "/platform/organisations", lazy: page(() => import("@/app/(platform)/platform/organisations/page")) },
          { path: "/platform/organisations/:orgId", lazy: page(() => import("@/app/(platform)/platform/organisations/[orgId]/page")) },
          // S-1063 on S-1043 (a): the platform's AI settings, and one organisation's AI limits with the override.
          // Authorised at the gateway on the platform relation; a caller without it is shown the 403.
          { path: "/platform/ai", lazy: page(() => import("@/app/(platform)/platform/ai/page")) },
          { path: "/platform/organisations/:orgId/ai", lazy: page(() => import("@/app/(platform)/platform/organisations/[orgId]/ai/page")) },
          // 3.116 (the sponsor, 2026-10-08): no Users or Settings page. Neither
          // managed anything: platform accounts live in the identity provider,
          // and every platform setting in the deployment's configuration.
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
